import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';

interface DocumentNode {
  nodeType: string;
  value?: string;
  content?: DocumentNode[];
}

/**
 * Recursively extracts text from any Contentful Document structure, regardless of block type.
 * This function traverses the document tree and concatenates all text node values.
 *
 * @param node - The Document node or any part of the document structure
 * @returns Extracted text content as a string, or empty string if no text found
 */
export const extractTextFromDocument = (node: unknown): string => {
  if (!node || typeof node !== 'object') return '';

  const documentNode = node as DocumentNode;

  // If it's a text node, return its value
  if (documentNode.nodeType === 'text' && documentNode.value) {
    return documentNode.value;
  }

  // If it has content array, recursively extract text from all children
  if (documentNode.content && Array.isArray(documentNode.content)) {
    return documentNode.content
      .map((child: DocumentNode) => extractTextFromDocument(child))
      .join(' ')
      .trim();
  }

  return '';
};

// Extracts React components from a Contentful Document using the provided render options
export const extractReactComponentsFromField = (
  field: Document | undefined,
  renderOptions: Options,
): React.ReactNode => {
  if (
    !field ||
    typeof field !== 'object' ||
    field.nodeType !== BLOCKS.DOCUMENT
  ) {
    throw new Error('Invalid Contentful Document structure');
  }

  return documentToReactComponents(field, renderOptions);
};
